// HoursField — pick a time. There is no box to mistype a decimal into.
//
// Owner, 2026-09-16: "For when workers need to make adjustment to the time when
// submitting the hours, make sure to show a clock or something for them to pick
// hours, this way we avoid someone misstyping."
//
// What they were looking at when they wrote that: a line reading 2.83, on a
// page where every other number was H:MM. hoursPicker.js carries the full
// reading of that screenshot and why one screen speaking two units produced
// all three of the complaints in it.
//
// Two <select>s, because on a phone that is one tap each and an <option> is the
// one label that cannot be squeezed off the screen by its neighbour. The same
// reason SheetDetail's kind picker and day picker are selects.
//
// ── It computes no money ───────────────────────────────────────────────────
//
// Not even a preview. Every figure on these screens is a return value of
// server/utils/paystubCalc.js; a pay figure worked out on a screen is #202's
// shape. What it shows back is the TIME, in the same H:MM the grid uses, so a
// person can see that the thing they picked is the thing the table will show.

import React from 'react';
import { FwSelect, FwButton, Notice } from '../../ui/fw';
import {
  MINUTE_CHOICES, hoursToParts, partsToHours, hourChoicesFor, clockLabel, offGridNotice,
  snapToGrid,
} from './hoursPicker';
import './HoursField.css';

/**
 * @param {object}   props
 * @param {string}   props.id          the hours select's id, for the label
 * @param {number}   props.value       decimal hours, as the database stores them
 * @param {Function} props.onChange    called with the new decimal hours
 * @param {number}   [props.clockHours] what the clock recorded, if anything
 * @param {string}   [props.label]     names the GROUP; defaults to "Hours"
 * @param {string}   [props.hint]      one line under the control, if any
 * @param {boolean}  [props.required]  marks the group, as FwField would
 */
export function HoursField({ id, value, onChange, clockHours, label = 'Hours', hint, required }) {
  const { hours, minutes } = hoursToParts(value);
  const hourChoices = hourChoicesFor(value);
  // Read from the CURRENT value, not from what was first passed in: the notice
  // must disappear the moment somebody picks something, or it goes on claiming
  // a line is off the grid after they have fixed it.
  const offGrid = offGridNotice(value);

  const setHours = (e) => onChange(partsToHours(Number(e.target.value), minutes));
  const setMinutes = (e) => onChange(partsToHours(hours, Number(e.target.value)));
  const onGrid = offGrid ? snapToGrid(value) : null;

  // The hint and the notice are both described-by text, and naming only the
  // total meant a screen reader announced the time and nothing else — not
  // "These hours are what payroll pays", not the off-grid sentence. Built
  // from what is actually RENDERED, so an id is never announced for an element
  // that is not there.
  const describedBy = [
    `${id}-total`,
    hint ? `${id}-hint` : null,
    offGrid ? `${id}-offgrid` : null,
  ].filter(Boolean).join(' ');

  return (
    // A GROUP with a name, not a field with a heading. An outer "Hours" label
    // above two controls labelled "Hours" and "Minutes" reads "Hours Hours
    // Minutes" — which a screen reader says out loud and a test caught in the
    // first run of this component. The two unit labels are the visible naming;
    // this carries the same word to anything that needs the group named.
    <div className="fw-field sh-hours" role="group" aria-label={label}>
      {/* No px anywhere: each control is `flex: 1 1 0` with `min-width: 0`, so
          the pair shares whatever room there is and neither can pan the page
          (docs/APP_REBUILD.md rule 2). */}
      <div className="sh-hours__row">
        <span className="sh-hours__part">
          {/* The asterisk goes on the FIRST control, not on the group: this
              renders no heading of its own (see the role="group" note), so
              there is nowhere else for FwField's marker to live. The field
              add form carried `required` before it became a picker and lost
              it in the conversion — restored here. */}
          <label className="sh-hours__unit" htmlFor={id}>
            Hours
            {required ? <span className="fw-field__required" aria-hidden="true"> *</span> : null}
          </label>
          {/* aria-required lives on the CONTROL, not on the group: it is not a
              supported attribute of role="group" and jsx-a11y refuses it,
              correctly — the zero-warning build caught that. */}
          <FwSelect id={id} value={hours} onChange={setHours} aria-describedby={describedBy} aria-required={required ? 'true' : undefined}>
            {hourChoices.map((h) => <option key={h} value={h}>{h}</option>)}
          </FwSelect>
        </span>
        <span className="sh-hours__part">
          <label className="sh-hours__unit" htmlFor={`${id}-minutes`}>Minutes</label>
          <FwSelect id={`${id}-minutes`} value={minutes} onChange={setMinutes} aria-describedby={describedBy}>
            {MINUTE_CHOICES.map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
            ))}
          </FwSelect>
        </span>
      </div>
      {/* The same reading the grid, the week total and the change history use.
          It is here so a person can see the form and the table agree BEFORE
          they save, which is exactly what the old decimal box denied them.

          THE LINE'S OWN TIME, not the selects'. For every value the picker can
          hold these are the same number. They differ only for a legacy line
          off the quarter grid, and there this has to tell the truth: the line
          IS 2:50, the selects can only say 2:45, and nothing moves it unless
          somebody picks. offGridNotice is what reconciles the two, and it
          promises no move — an earlier version promised one and the save path
          did not make it, then made it and took an Adjusted mark off a line
          nobody had touched. */}
      <div className="sh-hours__total fw-num" id={`${id}-total`} data-hours-total>
        {clockLabel(value)}
        {clockHours != null ? (
          <span className="sh-hours__clock" data-hours-clock>
            {' · Clock said '}
            <span className="fw-num">{clockLabel(clockHours)}</span>
          </span>
        ) : null}
      </div>
      {/* Its own hint, so no caller has to wrap this in an FwField — which
          would put a THIRD "Hours" above the two the controls already carry,
          and hand the group's id to a div. */}
      {hint ? <div className="fw-field__hint" id={`${id}-hint`}>{hint}</div> : null}
      {offGrid ? (
        <Notice tone="info" data-hours-offgrid>
          {/* The id goes on a span we own, not on Notice. Notice spreads
              `...rest` so an `id` would in fact land on its div — but it does
              not DECLARE one, and rebuildRenderHazards.test.js refuses props a
              primitive has not declared, because that is how a prop somebody
              thought was handled becomes a stray DOM attribute. It caught this
              within the minute. Describing the sentence alone is better
              anyway: aria-describedby on the whole Notice would read the
              button's label out as part of the explanation. */}
          <span id={`${id}-offgrid`}>{offGrid}</span>
          {/* ── The way to ACT on that sentence, in one press ───────────────
              Without this the notice named 2:45 and there was no way to get
              there: the selects already READ 2 and 45 for a line stored 2.83,
              so picking 45 again fires no change event and the value stays
              2.83. Somebody following the sentence had to pick another minute
              and come back. Two taps, on precisely the row that prompted this
              work. Found in review 2026-09-16.

              A BUTTON and not an effect, which is the whole distinction this
              component has been round twice on. Snapping on mount, or at save,
              moves a stored payroll figure because somebody opened or closed a
              screen — and it took an Adjusted mark off an untouched line while
              the pay fell $1.60. Pressing this is a decision, made by a person,
              on a control that says what it will do. Nothing happens unless
              they press it. */}
          <FwButton
            variant="ghost"
            className="sh-hours__snap"
            data-hours-snap
            onClick={() => onChange(onGrid)}
          >
            {`Set to ${clockLabel(onGrid)}`}
          </FwButton>
        </Notice>
      ) : null}
    </div>
  );
}

export default HoursField;
